import { Box, Heading, Spinner, Text, VStack } from "@chakra-ui/react"
import { useAlertConfiguration } from "../../hooks/useAlertConfiguration"
import { AssetAlertGroup } from "./AssetAlertGroup"
import { SystemAlertGroup } from "./SystemAlertGroup"

interface AlertConfigurationProps {
  systemId: string
}

export function AlertConfiguration({ systemId }: AlertConfigurationProps) {
  const {
    assetGroups,
    systemRules,
    loading,
    error,
    enableRule,
    disableRule,
    updateThreshold,
    updateTimeWindow,
    resetToDefaults,
  } = useAlertConfiguration(systemId)

  if (loading) {
    return (
      <Box p={6} display="flex" justifyContent="center">
        <Spinner size="lg" />
      </Box>
    )
  }

  if (error) {
    return (
      <Box p={6}>
        <Text color="red.500">Failed to load alert configuration: {error}</Text>
      </Box>
    )
  }

  const hasRules = assetGroups.some(g => g.totalCount > 0) || systemRules.length > 0

  if (!hasRules) {
    return (
      <Box p={6}>
        <Text>
          No alert rules are available for this system. Contact your administrator to configure alerts.
        </Text>
      </Box>
    )
  }

  const assetsWithAlerts = assetGroups.filter((g) => g.totalCount > 0)

  return (
    <VStack gap={6} align="stretch" p={4}>
      <Text fontSize="md">
        Configure which alerts are active for this system. Each asset&apos;s alerts are managed on that asset.
      </Text>

      {/* System-level rules: Standard Alerts only, with no custom alerts at this level */}
      {systemRules.length > 0 && (
        <VStack gap={3} align="stretch">
          <Heading as="h2" fontSize="lg" fontWeight="semibold">
            System alerts
          </Heading>
          <SystemAlertGroup
            systemRules={systemRules}
            systemId={systemId}
            onEnable={(templateId) => enableRule(templateId, systemId, "system")}
            onDisable={disableRule}
            onUpdateThreshold={(assignmentId, value, templateId) =>
              updateThreshold(assignmentId, value, templateId, systemId, "system")
            }
            onUpdateTimeWindow={(assignmentId, value, templateId) =>
              updateTimeWindow(assignmentId, value, templateId, systemId, "system")
            }
            onReset={resetToDefaults}
          />
        </VStack>
      )}

      {/* Asset-level rules, read-only here */}
      {assetsWithAlerts.length > 0 && (
        <VStack gap={3} align="stretch">
          <Heading as="h2" fontSize="lg" fontWeight="semibold">
            Asset alerts
          </Heading>
          {assetsWithAlerts.map((group) => (
            <AssetAlertGroup key={group.asset.id} group={group} />
          ))}
        </VStack>
      )}
    </VStack>
  )
}
